import { useState } from "react";
import { Link } from "react-router-dom";
import { MdNotifications } from "react-icons/md";
import { BiHomeAlt, BiStats, BiUser } from "react-icons/bi";
import logo from '../assets/img/svg/logo2.svg';
import { APP_CONSTANTS } from "../constants";
import { Divider, Drawer, IconButton, styled } from "@mui/material";
import { HiOutlineBellAlert } from "react-icons/hi2";
import { FaChevronDown } from "react-icons/fa";

const drawerWidth = 280;

interface NavBarProps {
  onToggleDrawer: (open: boolean) => void;
  isDrawerOpen: boolean;
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const NavBar: React.FC<NavBarProps> = ({ onToggleDrawer, isDrawerOpen }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(0); // Index des éléments principaux
  const [selectedSubIndices, setSelectedSubIndices] = useState<{ [key: number]: number | null }>({}); // Index des sous-éléments par groupe
  const [openSubmenus, setOpenSubmenus] = useState<boolean[]>([]);

  const navigationItems = [
    {
      label: "Tableau de bord",
      path: APP_CONSTANTS.ROUTES.HOME,
      icon: <MdNotifications size={22} />,
    },
    {
      label: "Gestion des utilisateurs",
      path: APP_CONSTANTS.ROUTES.USERS_MANAGEMENT,
      icon: <BiUser size={22} />,
    },
    {
      label: "Statistiques",
      icon: <BiStats size={22} />,
      subItems: [
        { label: "Performance des personnels", path: `${APP_CONSTANTS.ROUTES.HOME}` },
        { label: "Rapport de télé-expertise", path: `${APP_CONSTANTS.ROUTES.HOME}` },
        { label: "Patients", path: `${APP_CONSTANTS.ROUTES.HOME}` },
      ],
    },
    {
      label: "Gestion des centers",
      icon: <BiHomeAlt size={22} />,
      subItems: [
        { label: "Aperçu global", path: `${APP_CONSTANTS.ROUTES.HOME}` },
        { label: "Gestion des centres", path: `${APP_CONSTANTS.ROUTES.HOME}` },
      ],
    },
    {
      label: "Centre de notifications",
      path: `${APP_CONSTANTS.ROUTES.HOME}`,
      icon: <HiOutlineBellAlert size={22} />,
    },
  ];

  const handleItemClick = (index: number | null, path?: string) => {
    setSelectedSubIndices({})
    setSelectedIndex(null);
    setSelectedIndex(index);
    setSelectedSubIndices((prev) => ({ ...prev, [index!]: null })); // Reset sub-item selection for the clicked main item
    console.log(`Navigate to: ${path}`);
  };

  const handleSubItemClick = (mainIndex: number, subIndex: number | null, path?: string) => {
    setSelectedSubIndices({})
    setSelectedIndex(null);
    setSelectedSubIndices((prev) => ({
      ...prev,
      [mainIndex]: subIndex, // Set sub-item selection for the specific main item
    }));
    console.log(`Navigate to sub-item: ${path}`);
  };

  const toggleSubmenu = (index: number) => {
    setSelectedSubIndices({})
    setSelectedIndex(null);
    setOpenSubmenus((prevOpenSubmenus) => {
      const newOpenSubmenus = [...prevOpenSubmenus];
      newOpenSubmenus[index] = !newOpenSubmenus[index];
      return newOpenSubmenus;
    });
  };

  const handleDrawerClose = () => {
    onToggleDrawer(false);
  };

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
        },
      }}
      variant="persistent"
      anchor="left"
      open={isDrawerOpen}
    >
      <DrawerHeader>
        <IconButton onClick={handleDrawerClose} className="hover:bg-none">
          <img src={logo} />
        </IconButton>
      </DrawerHeader>
      <Divider />
      <ul className="space-y-2 font-medium">
        {navigationItems.map((item, index) => (
          <li key={index} className="m-4">
            {item?.subItems ? (
              <div
                onClick={() => toggleSubmenu(index)}
                className={
                  selectedIndex === index
                    ? "flex items-center bg-[#20A8D3] p-2 text-white rounded-lg dark:text-white dark:hover:bg-gray-400 group"
                    : "flex items-center p-2 text-[#838696] rounded-lg hover:text-Bleu dark:text-white hover:bg-gray-100 dark:hover:bg-gray-400 group"
                }
              >
                {item.icon}
                <span className="mx-3 cursor-pointer">{item.label} </span>
                <FaChevronDown className="cursor-pointer" />
              </div>
            ) : (
              <Link
                to={item.path}
                onClick={() => handleItemClick(index, item.path)}
                className={
                  selectedIndex === index
                    ? "flex items-center bg-[#20A8D3] p-2 text-white rounded-lg dark:text-white dark:hover:bg-gray-400 group"
                    : "flex items-center p-2 text-[#838696] rounded-lg hover:text-Bleu dark:text-white hover:bg-gray-100 dark:hover:bg-gray-400 group"
                }
              >
                {item.icon}
                <span className="ml-3">{item.label}</span>
              </Link>
            )}

            {item?.subItems && (
              <ul className={`ml-4 ${openSubmenus[index] ? "block" : "hidden"}`}>
                {item.subItems.map((subItem, subIndex) => (
                  <li key={subIndex}>
                    <Link
                      to={subItem.path}
                      onClick={() => handleSubItemClick(index, subIndex, subItem.path)}
                      className={
                        selectedSubIndices[index] === subIndex
                          ? "flex items-center bg-[#20A8D3] p-2 text-white rounded-lg dark:text-white dark:hover:bg-gray-400 group"
                          : "flex items-center p-2 text-[#838696] rounded-lg hover:text-Bleu dark:text-white hover:bg-gray-100 dark:hover:bg-gray-400 group"
                      }
                    >
                      {subItem.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Drawer>
  );
};

export default NavBar;
